import React, { useState, useEffect, useRef, useCallback } from 'react';
import { SERVER_URL } from '../data/serverStore';
import { auth } from '../data/auth';

const dmChannelId = (a, b) => `dm_${[a, b].sort().join('__')}`;
const wsUrl = () => SERVER_URL.replace(/^http/, 'ws');

// A hung fetch here (e.g. mid network-change/reconnect) previously had no
// timeout — combined with getChatUnreadCount's sequential per-channel loop
// below and an 8s poll with no overlap guard (fixed in App.jsx), this was
// the main path to a tab that never recovers from a brief network hiccup.
function fetchWithTimeout(url, ms = 8000) {
  const controller = new AbortController();
  const t = setTimeout(() => controller.abort(), ms);
  return fetch(url, { signal: controller.signal }).finally(() => clearTimeout(t));
}

async function fetchHistory(channelId) {
  try {
    const res = await fetchWithTimeout(`${SERVER_URL}/api/chat-history/${channelId}`);
    const data = await res.json();
    return Array.isArray(data.messages) ? data.messages : [];
  } catch { return []; }
}

async function fetchChannels(username) {
  try {
    const res = await fetchWithTimeout(`${SERVER_URL}/api/chat-channels?username=${encodeURIComponent(username || '')}`);
    const data = await res.json();
    return Array.isArray(data.channels) ? data.channels : [];
  } catch { return []; }
}

// ── Unread badge for the nav tab — REST-based on purpose, since this needs
// to work even when TeamChat itself isn't mounted (e.g. viewing another
// screen), where there's no live WebSocket connection open to check against.
export async function getChatUnreadCount(session) {
  if (!session) return 0;
  const myId = session.username;
  try {
    const [users, chs] = await Promise.all([auth.getUsersAsync(), fetchChannels(myId)]);
    const channelIds = (chs.length > 0 ? chs.map(c => c.id) : ['all_staff']);
    const channels = [...channelIds, ...(users || [])
      .filter(u => u.username !== myId && u.active !== false)
      .map(u => dmChannelId(myId, u.username))];
    // Previously sequential (one full round-trip per channel, awaited one
    // at a time) — under a flaky connection that multiplies the delay by
    // the number of channels/DM partners instead of just paying it once.
    const histories = await Promise.all(channels.map(ch => fetchHistory(ch)));
    let count = 0;
    histories.forEach((msgs, i) => {
      if (msgs.length === 0) return;
      const last = msgs[msgs.length - 1];
      const lastRead = parseInt(localStorage.getItem(`chat_read_${myId}_${channels[i]}`) || '0', 10);
      if (last.ts > lastRead && last.from !== myId) count++;
    });
    return count;
  } catch { return 0; }
}

export default function TeamChat({ session, toast }) {
  const myId = session.username;
  const myName = session.displayName || session.username;

  const [users, setUsers] = useState([]);
  const [channels, setChannels] = useState([{ id: 'all_staff', name: 'All Staff', createdBy: 'system', createdAt: 0 }]);
  const [showNewChannel, setShowNewChannel] = useState(false);
  const [newChannelName, setNewChannelName] = useState('');
  const [newChannelPrivate, setNewChannelPrivate] = useState(false);
  const [newChannelMembers, setNewChannelMembers] = useState(new Set());
  const [activeChannel, setActiveChannel] = useState('all_staff');
  const activeChannelRef = useRef('all_staff');
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [unreadMap, setUnreadMap] = useState({});
  const [online, setOnline] = useState([]);
  const [typingFrom, setTypingFrom] = useState(null); // username currently typing in the active channel
  const [wsStatus, setWsStatus] = useState('connecting'); // connecting | live | offline
  const [loading, setLoading] = useState(true);

  const wsRef = useRef(null);
  const bottomRef = useRef();
  const typingTimeoutRef = useRef(null);
  const typingSendThrottleRef = useRef(0);
  const reconnectRef = useRef(null);

  useEffect(() => { activeChannelRef.current = activeChannel; }, [activeChannel]);

  // Staff roster
  useEffect(() => {
    auth.getUsersAsync().then(u => {
      setUsers((u || []).filter(x => x.username !== myId && x.active !== false));
    }).catch(() => {});
  }, [myId]);

  // Channel list
  useEffect(() => {
    fetchChannels(myId).then(chs => { if (chs.length > 0) setChannels(chs); });
  }, [myId]);

  // ── WebSocket connection — connect once, auto-reconnect if dropped ───────
  useEffect(() => {
    let stopped = false;

    const connect = () => {
      if (stopped) return;
      setWsStatus('connecting');
      const ws = new WebSocket(wsUrl());
      wsRef.current = ws;

      ws.onopen = () => {
        setWsStatus('live');
        ws.send(JSON.stringify({ type: 'auth', username: myId }));
      };

      ws.onmessage = (evt) => {
        let data;
        try { data = JSON.parse(evt.data); } catch { return; }

        if (data.type === 'presence') {
          setOnline(data.online || []);
          return;
        }

        if (data.type === 'channel_created') {
          setChannels(prev => prev.some(c => c.id === data.channel.id) ? prev : [...prev, data.channel]);
          return;
        }

        if (data.type === 'typing') {
          if (data.channel !== activeChannelRef.current || data.from === myId) return;
          setTypingFrom(data.from);
          clearTimeout(typingTimeoutRef.current);
          typingTimeoutRef.current = setTimeout(() => setTypingFrom(null), 2500);
          return;
        }

        if (data.type === 'message') {
          if (data.channel === activeChannelRef.current) {
            setMessages(prev => prev.some(m => m.id === data.message.id) ? prev : [...prev, data.message]);
          } else if (data.message.from !== myId) {
            setUnreadMap(prev => ({ ...prev, [data.channel]: true }));
          }
          return;
        }
      };

      ws.onclose = () => {
        if (stopped) return;
        setWsStatus('offline');
        wsRef.current = null;
        reconnectRef.current = setTimeout(connect, 3000);
      };

      ws.onerror = () => { ws.close(); };
    };

    connect();
    return () => {
      stopped = true;
      clearTimeout(reconnectRef.current);
      wsRef.current?.close();
    };
  }, [myId]);

  // Load history whenever the open channel changes
  useEffect(() => {
    let stopped = false;
    setLoading(true);
    fetchHistory(activeChannel).then(msgs => { if (!stopped) { setMessages(msgs); setLoading(false); } });
    return () => { stopped = true; };
  }, [activeChannel]);

  // Mark the open channel as read whenever its latest message changes
  useEffect(() => {
    if (messages.length === 0) return;
    const last = messages[messages.length - 1];
    localStorage.setItem(`chat_read_${myId}_${activeChannel}`, String(last.ts));
    setUnreadMap(prev => (prev[activeChannel] ? { ...prev, [activeChannel]: false } : prev));
  }, [messages, activeChannel, myId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const send = useCallback(() => {
    const text = draft.trim();
    if (!text) return;
    setDraft('');
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'message', channel: activeChannel, text, fromName: myName }));
    } else {
      toast && toast('error', 'Not connected — message not sent. Reconnecting...');
    }
  }, [draft, activeChannel, myName, toast]);

  const handleTyping = useCallback((val) => {
    setDraft(val);
    const now = Date.now();
    if (now - typingSendThrottleRef.current > 1500 && wsRef.current?.readyState === WebSocket.OPEN) {
      typingSendThrottleRef.current = now;
      wsRef.current.send(JSON.stringify({ type: 'typing', channel: activeChannel }));
    }
  }, [activeChannel]);

  const createChannel = useCallback(() => {
    const name = newChannelName.trim();
    if (!name) return;
    if (newChannelPrivate && newChannelMembers.size === 0) {
      toast && toast('error', 'Pick at least one person to invite for a private channel');
      return;
    }
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'create_channel', name,
        private: newChannelPrivate,
        members: [...newChannelMembers],
      }));
      setNewChannelName('');
      setNewChannelPrivate(false);
      setNewChannelMembers(new Set());
      setShowNewChannel(false);
    } else {
      toast && toast('error', 'Not connected — try again in a moment');
    }
  }, [newChannelName, newChannelPrivate, newChannelMembers, toast]);

  const toggleMember = useCallback((username) => {
    setNewChannelMembers(prev => {
      const next = new Set(prev);
      if (next.has(username)) next.delete(username); else next.add(username);
      return next;
    });
  }, []);

  const activeUser = users.find(u => dmChannelId(myId, u.username) === activeChannel);
  const activeChannelObj = channels.find(c => c.id === activeChannel);
  const activeLabel = activeChannelObj
    ? `${activeChannel === 'all_staff' ? '📢' : activeChannelObj.private ? '🔒' : '#'} ${activeChannelObj.name}`
    : `💬 ${activeUser?.displayName || activeUser?.username || 'Direct Message'}`;

  const STATUS_DOT = { connecting: '#e0a000', live: '#1a6b42', offline: '#dc2626' };
  const STATUS_LABEL = { connecting: 'Connecting...', live: 'Live', offline: 'Reconnecting...' };

  return (
    <div className="content" style={{ display: 'flex', height: 'calc(100vh - 140px)', padding: 0, gap: 0 }}>
      {/* Sidebar */}
      <div style={{ width: 220, borderRight: '1px solid #e0e4ec', overflowY: 'auto', flexShrink: 0, background: '#fafbfd' }}>
        <div style={{ padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 6,
          borderBottom: '1px solid #eee', marginBottom: 4 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: STATUS_DOT[wsStatus] }} />
          <span style={{ fontSize: 10, color: '#888' }}>{STATUS_LABEL[wsStatus]}</span>
        </div>

        <div style={{ padding: '10px 12px 6px', fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Channels
          <button onClick={() => setShowNewChannel(v => !v)} title="Create a new channel"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1e3060', fontSize: 15, fontWeight: 700, lineHeight: 1 }}>
            +
          </button>
        </div>
        {showNewChannel && (
          <div style={{ padding: '4px 12px 12px', border: '1px solid #e0e4ec', borderRadius: 6, margin: '0 8px 8px', background: 'white' }}>
            <input value={newChannelName} onChange={e => setNewChannelName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Escape') setShowNewChannel(false); }}
              placeholder="channel-name" autoFocus
              style={{ width: '100%', border: '1px solid #cdd3e4', borderRadius: 4, padding: '5px 7px',
                fontSize: 12, outline: 'none', marginTop: 8, marginBottom: 8, boxSizing: 'border-box' }} />
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, cursor: 'pointer', marginBottom: 6 }}>
              <input type="checkbox" checked={newChannelPrivate}
                onChange={e => { setNewChannelPrivate(e.target.checked); if (!e.target.checked) setNewChannelMembers(new Set()); }} />
              🔒 Private (invite-only)
            </label>
            {newChannelPrivate && (
              <div style={{ maxHeight: 120, overflowY: 'auto', border: '1px solid #eee', borderRadius: 4, marginBottom: 8, padding: 4 }}>
                {users.length === 0 && <div style={{ fontSize: 11, color: '#aaa', padding: 4 }}>No other staff to invite</div>}
                {users.map(u => (
                  <label key={u.username} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, padding: '3px 4px', cursor: 'pointer' }}>
                    <input type="checkbox" checked={newChannelMembers.has(u.username)}
                      onChange={() => toggleMember(u.username)} />
                    {u.displayName || u.username}
                  </label>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={createChannel} disabled={!newChannelName.trim()}
                style={{ flex: 1, background: '#1e3060', color: 'white', border: 'none', borderRadius: 4, padding: '6px 10px',
                  cursor: newChannelName.trim() ? 'pointer' : 'not-allowed', fontSize: 12, fontWeight: 600 }}>
                Create
              </button>
              <button onClick={() => setShowNewChannel(false)}
                style={{ background: '#f0f2f6', border: 'none', borderRadius: 4, padding: '6px 10px', fontSize: 12, cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        )}
        {channels.map(ch => (
          <button key={ch.id} onClick={() => setActiveChannel(ch.id)}
            style={{ width: '100%', textAlign: 'left', padding: '9px 12px', border: 'none', cursor: 'pointer',
              background: activeChannel === ch.id ? '#e8f0ff' : 'transparent',
              fontWeight: activeChannel === ch.id ? 700 : 400, fontSize: 13,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {ch.id === 'all_staff' ? '📢' : ch.private ? '🔒' : '#'} {ch.name}
            </span>
            {unreadMap[ch.id] && <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#dc2626', flexShrink: 0 }} />}
          </button>
        ))}

        <div style={{ padding: '14px 12px 6px', fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase' }}>
          Direct Messages
        </div>
        {users.length === 0 && (
          <div style={{ padding: '8px 12px', fontSize: 11, color: '#aaa' }}>No other active staff yet</div>
        )}
        {users.map(u => {
          const ch = dmChannelId(myId, u.username);
          const isOnline = online.includes(u.username);
          return (
            <button key={u.username} onClick={() => setActiveChannel(ch)}
              style={{ width: '100%', textAlign: 'left', padding: '9px 12px', border: 'none', cursor: 'pointer',
                background: activeChannel === ch ? '#e8f0ff' : 'transparent',
                fontWeight: activeChannel === ch ? 700 : 400, fontSize: 13,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
                  background: isOnline ? '#1a6b42' : '#ccc' }} title={isOnline ? 'Online' : 'Offline'} />
                {u.displayName || u.username}
              </span>
              {unreadMap[ch] && <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#dc2626', flexShrink: 0 }} />}
            </button>
          );
        })}
      </div>

      {/* Messages */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <div style={{ padding: '12px 18px', borderBottom: '1px solid #e0e4ec', fontWeight: 700, fontSize: 14, flexShrink: 0,
          display: 'flex', alignItems: 'center', gap: 10 }}>
          {activeLabel}
          {activeUser && (
            <span style={{ fontSize: 10, fontWeight: 400, color: online.includes(activeUser.username) ? '#1a6b42' : '#aaa' }}>
              {online.includes(activeUser.username) ? '● Online' : '○ Offline'}
            </span>
          )}
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {loading ? (
            <div style={{ color: '#aaa', fontSize: 12, textAlign: 'center', marginTop: 20 }}>Loading...</div>
          ) : messages.length === 0 ? (
            <div style={{ color: '#aaa', fontSize: 12, textAlign: 'center', marginTop: 20 }}>
              No messages yet — say hello 👋
            </div>
          ) : messages.map(m => (
            <div key={m.id} style={{ alignSelf: m.from === myId ? 'flex-end' : 'flex-start', maxWidth: '70%' }}>
              <div style={{ fontSize: 10, color: '#888', marginBottom: 2, textAlign: m.from === myId ? 'right' : 'left' }}>
                {m.from === myId ? 'You' : m.fromName} · {new Date(m.ts).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </div>
              <div style={{
                background: m.from === myId ? '#1e3060' : '#f0f2f6',
                color: m.from === myId ? 'white' : '#111',
                padding: '8px 12px', borderRadius: 10, fontSize: 13, whiteSpace: 'pre-wrap', lineHeight: 1.5,
              }}>
                {m.text}
              </div>
            </div>
          ))}
          {typingFrom && (
            <div style={{ fontSize: 11, color: '#aaa', fontStyle: 'italic' }}>
              {activeChannel === 'all_staff' ? (users.find(u => u.username === typingFrom)?.displayName || typingFrom) : (activeUser?.displayName || typingFrom)} is typing...
            </div>
          )}
          <div ref={bottomRef} />
        </div>
        <div style={{ display: 'flex', gap: 8, padding: 12, borderTop: '1px solid #e0e4ec', flexShrink: 0 }}>
          <textarea value={draft} onChange={e => handleTyping(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Type a message... (Enter to send, Shift+Enter for new line)"
            rows={2}
            style={{ flex: 1, border: '1px solid #cdd3e4', borderRadius: 6, padding: '8px 10px',
              fontSize: 13, resize: 'none', outline: 'none', fontFamily: 'inherit' }} />
          <button onClick={send} disabled={!draft.trim()}
            style={{ background: draft.trim() ? '#1e3060' : '#ccc', color: 'white', border: 'none',
              borderRadius: 6, padding: '0 22px', cursor: draft.trim() ? 'pointer' : 'not-allowed',
              fontWeight: 700, fontSize: 13 }}>
            Send
          </button>
        </div>
      </div>
    </div>
  );
}
